import create from "zustand";
import { produce } from "immer";

interface DentalExam {
  ChiefComplaints: any;
  OralHygiene: any;
  DmftIndex: object;
  MalocclusionData: any;
  StainsData: any;
  CalculusData: any;
  DentalMetaData: any;
  SuggestedInvestigationData: any;
  DentalsOtherData: any;
  NeedAttention: any;
}

export interface DentalState extends DentalExam {
  setDentalReportData: (responseData: any) => void;
  resetStore: () => void;
}

const initialState: DentalExam = {
  ChiefComplaints: {
    Chief_Complaint_Bleeding_Gums: false,
    Chief_Complaint_Decayed_Teeth: false,
    Chief_Complaint_Inflamed_Gums: false,
    Chief_Complaint_Tooth_Pain: false,
  },
  DentalsOtherData: {
    Chief_Complaint_Others: "",
    Dental_Other_Tests: "",
    Habits_Others: "",
  },
  DmftIndex: {},
  MalocclusionData: {
    Malocclusion_Class_1: false,
    Malocclusion_Class_2: false,
    Malocclusion_Class_3: false,
  },
  StainsData: {
    Stains_Grade_1: false,
    Stains_Grade_2: false,
    Stains_Grade_3: false,
  },
  CalculusData: {
    Calculus_Grade_1: false,
    Calculus_Grade_2: false,
    Calculus_Grade_3: false,
  },
  OralHygiene: {
    Oral_Hygiene_Poor: false,
    Habits_Thumb_Sucking: false,
    Habits_Bottle_Sucking: false,
    Habits_Mouth_Breathing: false,
    Habits_Brushing_once_day: false,
  },
  DentalMetaData: {
    Dental_Doctor_No: "",
    Dental_Doctor_Designation: "",
    Dental_Remarks: "",
    Dental_Doctor_Path: "",
    Dental_Doctor_Hospital: "",
  },
  SuggestedInvestigationData: {
    Dental_Doctor_Consultation: false,
    Scaling: false,
    Filling: false,
    Orthodontic_Treatment: false,
    Tooth_Rehabilitation: false,
    Extraction: false,
    IOPA: false,
    OPG: false,
    HSG_Change_Dental_Kit: false,
    HSG_Filling: false,
    HSG_OPG: false,
    HSG_Dental_Doctor_Consultation: false,
  },
  NeedAttention: {
    chiefComplaints: false,
    oralHygiene: false,
    malocclusion: false,
    stains: false,
    calculus: false,
    suggestedInvestigation: false,
    dmftIndex: false,
  },
};

// ... Keep your interfaces as they are

export const useDentalStore = create<DentalState>((set) => ({
  ...initialState,

  setDentalReportData: (res) =>
    set(
      produce((state: DentalState) => {
        const responseData = res?.data?.data;

        if (!responseData) return;

        const newNeedAttention = {
          chiefComplaints: false,
          oralHygiene: false,
          malocclusion: false,
          stains: false,
          calculus: false,
          suggestedInvestigation: false,
          dmftIndex: false,
        };

        const processBooleanSection = (
          sectionKey: string,
          attentionKey: string
        ) => {
          const section = state[sectionKey];
          Object.keys(section).forEach((key) => {
            const isActive = responseData[key] === "Y";
            if (isActive) newNeedAttention[attentionKey] = true;
            section[key] = isActive;
          });
        };

        processBooleanSection("ChiefComplaints", "chiefComplaints");
        processBooleanSection("OralHygiene", "oralHygiene");
        processBooleanSection("MalocclusionData", "malocclusion");
        processBooleanSection("StainsData", "stains");
        processBooleanSection("CalculusData", "calculus");
        processBooleanSection(
          "SuggestedInvestigationData",
          "suggestedInvestigation"
        );

        Object.keys(state.DentalMetaData).forEach((key) => {
          state.DentalMetaData[key] = responseData[key] || "";
        });

        Object.keys(state.DentalsOtherData).forEach((key) => {
          state.DentalsOtherData[key] = responseData[key] || "";
        });

        const newDmftIndex: Record<string, string> = {};
        for (const key in responseData) {
          if (key.match(/^Q\d+__\w+_\w+$/) && responseData[key] === "Y") {
            newNeedAttention.dmftIndex = true;
            const match = key.match(/^(Q\d)__([a-zA-Z0-9]+)_([DMF])\b/);
            if (match) {
              const [, qNum, toothId, status] = match;
              newDmftIndex[`${qNum}_${toothId}`] = status;
            }
          }
        }

        state.DmftIndex = newDmftIndex;
        state.NeedAttention = newNeedAttention;
      })
    ),

  resetStore: () => set(initialState),
}));
